// Section 2 and 24.13: one seeded PRNG stream in state (mulberry32), plus a separate event
// stream. 24.8: ExpectedValueRng implements the same interface for projections.
//
// Phases consume randomness only through these helpers; raw `next()` is not exported.
// `chance(p)` returns a number: 0 or 1 in live mode, p in projection mode. Branch with
// `happened(x)`; accumulate expectations by adding the number itself (24.8).
//
// Determinism across engines: only integer ops and + - * / are used. normal() uses the
// Irwin-Hall sum of 12 uniforms instead of Box-Muller so no transcendental Math functions
// (whose precision is engine-defined) touch the stream.
import { SimError } from './errors';

declare const rngResultBrand: unique symbol;
/** A chance outcome: 0 or 1 live, a probability in projection. Use `happened()` to branch. */
export type RngResult = number & { readonly [rngResultBrand]: true };

export interface WeightedItem<T> {
  item: T;
  weight: number;
}

export interface Rng {
  readonly mode: 'live' | 'projection';
  /** Live: 1 with probability p, else 0. Projection: p (clamped to 0..1). */
  chance(p: number): RngResult;
  /** Live: x >= 1. Projection: the most likely outcome (x >= 0.5). */
  happened(x: RngResult): boolean;
  /** Uniform in [0, 1). Projection: 0.5. */
  float(): number;
  /** Uniform integer in [min, max] inclusive. Projection: floor of the midpoint. */
  int(min: number, max: number): number;
  /** Uniform pick. Projection: the first candidate (callers pass sorted candidates). */
  pick<T>(items: readonly T[]): T;
  /** Weighted pick. Projection: the heaviest item (first on ties). */
  weighted<T>(items: readonly WeightedItem<T>[]): T;
  /** Approximately normal. Projection: the mean. */
  normal(mean: number, sd: number): number;
  /** Fisher-Yates shuffle into a new array. Projection: an unchanged copy. */
  shuffle<T>(items: readonly T[]): T[];
}

/** A live rng plus access to its serialisable position; only the runner and generators hold one. */
export interface RngHandle {
  rng: Rng;
  state(): number;
}

/** FNV-1a over UTF-16 code units, then a murmur3 finaliser for avalanche. Returns uint32. */
export function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Initial states of the main and event streams for a seed (24.13). */
export function seedStreams(seed: string): { main: number; events: number } {
  return { main: hashString(`${seed}#main`), events: hashString(`${seed}#events`) };
}

function assertNonEmpty(length: number, what: string): void {
  if (length === 0) throw new SimError('invariant', `rng.${what} called with no candidates`);
}

/** mulberry32 over a uint32 state. */
export function createRng(initialState: number): RngHandle {
  let s = initialState | 0;
  const next = (): number => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const rng: Rng = {
    mode: 'live',
    chance: (p) => (next() < p ? 1 : 0) as RngResult,
    happened: (x) => x >= 1,
    float: next,
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
    pick: (items) => {
      assertNonEmpty(items.length, 'pick');
      return items[Math.floor(next() * items.length)] as (typeof items)[number];
    },
    weighted: (items) => {
      assertNonEmpty(items.length, 'weighted');
      let total = 0;
      for (const entry of items) total += Math.max(0, entry.weight);
      if (total <= 0) throw new SimError('invariant', 'rng.weighted called with zero total weight');
      let roll = next() * total;
      for (const entry of items) {
        roll -= Math.max(0, entry.weight);
        if (roll < 0) return entry.item;
      }
      return (items[items.length - 1] as WeightedItem<(typeof items)[number]['item']>).item;
    },
    normal: (mean, sd) => {
      let sum = 0;
      for (let i = 0; i < 12; i++) sum += next();
      return mean + sd * (sum - 6);
    },
    shuffle: (items) => {
      const out = items.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        const a = out[i] as (typeof out)[number];
        out[i] = out[j] as (typeof out)[number];
        out[j] = a;
      }
      return out;
    },
  };
  return { rng, state: () => s >>> 0 };
}

/** 24.8: deterministic expectations for projections; consumes nothing. */
export function createExpectedValueRng(): Rng {
  return {
    mode: 'projection',
    chance: (p) => Math.min(1, Math.max(0, p)) as RngResult,
    happened: (x) => x >= 0.5,
    float: () => 0.5,
    int: (min, max) => Math.floor((min + max) / 2),
    pick: (items) => {
      assertNonEmpty(items.length, 'pick');
      return items[0] as (typeof items)[number];
    },
    weighted: (items) => {
      assertNonEmpty(items.length, 'weighted');
      let best = items[0] as WeightedItem<(typeof items)[number]['item']>;
      for (const entry of items) if (entry.weight > best.weight) best = entry;
      return best.item;
    },
    normal: (mean) => mean,
    shuffle: (items) => items.slice(),
  };
}
